import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, FileDown, Plus, RotateCw, Save, Settings, X } from "lucide-react";
import { apiFetch } from "../utils/apiFetch";
import { fetchOperatorNews } from "../services/news";
import { getToMeConversations } from "../services/chat";
import { listOperatorRequests } from "../services/requests";
import { listClientBilling } from "../services/billing";
import { fetchDevices, fetchLocations, fetchSockets } from "../services/devices";
import { fetchOperatorPolls } from "../services/polls";
import { listOperatorSensors } from "../services/sensors";
import { buildDoorEntries } from "../utils/doorEntries";
import { exportDebtorsPdf } from "../utils/exportDebtorsPdf";
import { API_BASE_OPERATOR as API_BASE } from "../config/vars";

const WIDGET_LAYOUT_KEY = "WR_OPERATOR_WIDGETS_LAYOUT";
const WIDGET_HINT_KEY = "WR_OPERATOR_WIDGETS_HINT_SEEN";
const DEFAULT_WIDGET_ORDER = [
    "billing",
    "debt",
    "info",
    "news",
    "polls",
    "chats",
    "requests",
    "pendingClients",
    "sensors",
    "door",
];

const readWidgetLayout = () => {
    try {
        const raw = localStorage.getItem(WIDGET_LAYOUT_KEY);
        if (!raw) return { order: [...DEFAULT_WIDGET_ORDER], hidden: [] };
        const parsed = JSON.parse(raw);
        const order = Array.isArray(parsed?.order)
            ? parsed.order.filter((id) => DEFAULT_WIDGET_ORDER.includes(id))
            : [...DEFAULT_WIDGET_ORDER];
        const hidden = Array.isArray(parsed?.hidden)
            ? parsed.hidden.filter((id) => DEFAULT_WIDGET_ORDER.includes(id))
            : [];
        const missing = DEFAULT_WIDGET_ORDER.filter((id) => !order.includes(id) && !hidden.includes(id));
        return { order: [...order, ...missing], hidden };
    } catch (_) {
        return { order: [...DEFAULT_WIDGET_ORDER], hidden: [] };
    }
};

const writeWidgetLayout = (layout) => {
    try {
        localStorage.setItem(WIDGET_LAYOUT_KEY, JSON.stringify(layout));
    } catch (_) {
        // ignore
    }
};

const formatMoney = (v) => {
    const n = Number(v);
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toFixed(2);
};

const WidgetCard = ({
    title,
    value,
    hint,
    onClick,
    className = "",
    hasBadge = false,
    badgeValue = null,
    editMode = false,
    onMoveUp,
    onMoveDown,
    onHide,
    canMoveUp = false,
    canMoveDown = false,
}) => {
    const clickable = !editMode && typeof onClick === "function";
    const numericBadge = Number(badgeValue);
    const hasNumericBadge = Number.isFinite(numericBadge) && numericBadge > 0;
    const showBadge = !editMode && Boolean(hasBadge || hasNumericBadge);
    const badgeLabel = hasNumericBadge ? numericBadge : 1;
    return (
        <div className={`widget-card-shell ${editMode ? "is-editing" : ""}`}>
            <div
                className={`widget-card ${clickable ? "widget-card--click" : ""} ${className}`}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? onClick : undefined}
                onKeyDown={clickable ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onClick?.(event);
                    }
                } : undefined}
            >
                {showBadge ? (
                    <span className="widget-card__badge widget-card__badge--count" aria-hidden="true">
                        {badgeLabel}
                    </span>
                ) : null}
                {editMode ? (
                    <div className="widget-card-edit-bar">
                        <button type="button" className="widget-card-edit-btn" onClick={onMoveUp} disabled={!canMoveUp} title="Вище" aria-label="Вище">
                            <ChevronUp size={16} />
                        </button>
                        <button type="button" className="widget-card-edit-btn" onClick={onMoveDown} disabled={!canMoveDown} title="Нижче" aria-label="Нижче">
                            <ChevronDown size={16} />
                        </button>
                        <button type="button" className="widget-card-edit-btn widget-card-edit-btn--danger" onClick={onHide} title="Приховати" aria-label="Приховати">
                            <X size={16} />
                        </button>
                    </div>
                ) : null}
                <div className="widget-card__title">{title}</div>
                <div className="widget-card__value">{value}</div>
                {hint ? <div className="widget-card__hint">{hint}</div> : null}
            </div>
        </div>
    );
};

const OperatorWidgets = ({
    onNavigate,
    pendingClientApprovalsCount = 0,
    hasNews = false,
    unreadNewsCount = 0,
    hasOperatorInfo = false,
    unreadOperatorInfoCount = 0,
    hasChats = false,
    unreadChatsCount = 0,
    hasPolls = false,
    unreadPollsCount = 0,
    openRequestsCountBadge = 0,
    sensorAccess = { page_enabled: false },
    networkFeatures = null,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [doorSockets, setDoorSockets] = useState([]);
    const [doorPanelOpen, setDoorPanelOpen] = useState(false);
    const [doorMessage, setDoorMessage] = useState("");
    const [debtPanelOpen, setDebtPanelOpen] = useState(false);

    const [infoCount, setInfoCount] = useState(0);
    const [newsCount, setNewsCount] = useState(0);
    const [activeChatsCount, setActiveChatsCount] = useState(0);
    const [openRequestsCount, setOpenRequestsCount] = useState(0);
    const [activePollsCount, setActivePollsCount] = useState(0);
    const [publicSensorsCount, setPublicSensorsCount] = useState(0);
    const [publicSensorAlertsCount, setPublicSensorAlertsCount] = useState(0);

    const [amountDueNetwork, setAmountDueNetwork] = useState(0);
    const [debtNetwork, setDebtNetwork] = useState(0);
    const [billingRows, setBillingRows] = useState([]);

    const [editMode, setEditMode] = useState(false);
    const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
    const [layout, setLayout] = useState(() => readWidgetLayout());
    const [layoutHintSeen, setLayoutHintSeen] = useState(() => {
        try {
            return localStorage.getItem(WIDGET_HINT_KEY) === "1";
        } catch (_) {
            return false;
        }
    });

    const persistLayout = useCallback((next) => {
        setLayout(next);
        writeWidgetLayout(next);
    }, []);

    const markLayoutHintSeen = useCallback(() => {
        setLayoutHintSeen(true);
        try {
            localStorage.setItem(WIDGET_HINT_KEY, "1");
        } catch (_) {
            // ignore
        }
    }, []);

    const unlockSocket = useCallback(async (socketId) => {
        const res = await apiFetch(`${API_BASE}/sockets/operator/${socketId}/unlock`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
        });

        let payload = null;
        try {
            payload = await res.json();
        } catch (_) {
            payload = null;
        }

        if (res.ok) return payload;

        const message = payload?.message || payload?.detail || "Не вдалося відкрити";
        throw new Error(message);
    }, []);

    const handleQuickUnlock = useCallback(async (socketId) => {
        setDoorMessage("");
        setError("");
        setLoading(true);
        try {
            const res = await unlockSocket(socketId);
            if (res && res.success) {
                setDoorMessage("✅ Підʼїзд відкрито");
            } else {
                setDoorMessage(`❌ ${res?.message || "Не вдалося відкрити"}`);
            }
        } catch (e) {
            setDoorMessage(`❌ ${e?.message || "Не вдалося відкрити"}`);
        } finally {
            setLoading(false);
        }
    }, [unlockSocket]);

    const handleDoorCardClick = useCallback(async () => {
        setDoorMessage("");
        setError("");
        setDebtPanelOpen(false);

        let doorEntries = doorSockets;
        if (!Array.isArray(doorEntries) || doorEntries.length === 0) {
            setLoading(true);
            try {
                const [devices, locations, sockets] = await Promise.all([
                    fetchDevices().catch(() => []),
                    fetchLocations().catch(() => []),
                    fetchSockets().catch(() => []),
                ]);
                doorEntries = buildDoorEntries({ devices, locations, sockets });
                setDoorSockets(doorEntries);
            } catch (e) {
                setError(e?.message || "Не вдалося завантажити підʼїзди");
                doorEntries = [];
            } finally {
                setLoading(false);
            }
        }

        if (doorEntries.length === 1) {
            await handleQuickUnlock(doorEntries[0].id);
            return;
        }

        setDoorPanelOpen((v) => !v);
    }, [doorSockets, handleQuickUnlock]);

    const debtors = useMemo(() => (
        (Array.isArray(billingRows) ? billingRows : [])
            .filter((row) => (Number(row?.debt_amount) || 0) > 0)
            .slice()
            .sort((a, b) => (Number(b?.debt_amount) || 0) - (Number(a?.debt_amount) || 0))
    ), [billingRows]);

    const handleDebtCardClick = useCallback(() => {
        setDoorPanelOpen(false);
        setDebtPanelOpen((v) => !v);
    }, []);

    const widgetCatalog = useMemo(() => {
        // null = features not loaded yet → fail-open (keep previous full catalog).
        const moduleOn = (featureKey) => (
            networkFeatures == null || networkFeatures[featureKey] !== false
        );
        const sensorsAvailable = Boolean(
            moduleOn("sensors") && sensorAccess.page_enabled && publicSensorsCount > 0
        );
        return {
            billing: {
                id: "billing",
                title: "Сума до оплати за утримання будинку",
                value: `${formatMoney(amountDueNetwork)} грн`,
                hint: "Сума по всій мережі",
                onClick: null,
                className: "widget-card--billing-top",
                available: moduleOn("billing"),
                kind: "card",
                fullWidth: true,
            },
            debt: {
                id: "debt",
                title: "Борг",
                value: `${formatMoney(debtNetwork)} грн`,
                hint: "Сума всіх боргів по мережі",
                onClick: handleDebtCardClick,
                available: moduleOn("billing"),
                kind: "card",
            },
            info: {
                id: "info",
                title: "Інформація",
                value: infoCount,
                hint: "Оголошення / повідомлення оператора",
                onClick: () => onNavigate && onNavigate("operator-info"),
                badgeValue: unreadOperatorInfoCount,
                available: moduleOn("operator_info"),
                kind: "card",
            },
            news: {
                id: "news",
                title: "Новини",
                value: newsCount,
                hint: "Новини для мережі",
                onClick: () => onNavigate && onNavigate("news"),
                badgeValue: unreadNewsCount,
                available: moduleOn("news"),
                kind: "card",
            },
            polls: {
                id: "polls",
                title: "Опитування",
                value: activePollsCount,
                hint: "Активні опитування",
                onClick: () => onNavigate && onNavigate("polls"),
                badgeValue: unreadPollsCount,
                available: moduleOn("polls"),
                kind: "card",
            },
            chats: {
                id: "chats",
                title: "Активні переписки",
                value: activeChatsCount,
                hint: "Переписки зі статусом 'відкрито'",
                onClick: () => onNavigate && onNavigate("chat"),
                badgeValue: unreadChatsCount,
                available: moduleOn("chat"),
                kind: "card",
            },
            requests: {
                id: "requests",
                title: "Заявки",
                value: openRequestsCount,
                hint: "Відкриті заявки",
                onClick: () => onNavigate && onNavigate("requests"),
                badgeValue: openRequestsCountBadge,
                available: moduleOn("requests"),
                kind: "card",
            },
            pendingClients: {
                id: "pendingClients",
                title: "Нові клієнти",
                value: pendingClientApprovalsCount,
                hint: pendingClientApprovalsCount > 0 ? "Очікують погодження підключення" : "Нових запитів на підключення нема",
                onClick: () => onNavigate && onNavigate("clients"),
                className: pendingClientApprovalsCount > 0 ? "widget-card--attention" : "",
                badgeValue: pendingClientApprovalsCount,
                available: moduleOn("clients"),
                kind: "card",
            },
            sensors: {
                id: "sensors",
                title: "Спрацювання сенсорів",
                value: publicSensorAlertsCount,
                hint: publicSensorAlertsCount > 0 ? "Публічні сенсори з тривогою або попередженням" : "Публічні сенсори без активних спрацювань",
                onClick: () => onNavigate && onNavigate("sensors"),
                className: publicSensorAlertsCount > 0 ? "widget-card--attention" : "",
                badgeValue: publicSensorAlertsCount,
                available: sensorsAvailable,
                kind: "card",
            },
            door: {
                id: "door",
                title: "Відкрити підʼїзд",
                value: doorSockets.length,
                hint: "Швидке відкривання дверей",
                onClick: handleDoorCardClick,
                available: moduleOn("door_access"),
                kind: "card",
            },
        };
    }, [
        amountDueNetwork, debtNetwork, handleDebtCardClick, infoCount, newsCount, activePollsCount,
        activeChatsCount, openRequestsCount, pendingClientApprovalsCount, onNavigate, doorSockets.length,
        handleDoorCardClick, unreadOperatorInfoCount, unreadNewsCount, unreadPollsCount, unreadChatsCount,
        openRequestsCountBadge, sensorAccess.page_enabled, publicSensorsCount, publicSensorAlertsCount,
        networkFeatures,
    ]);

    const visibleWidgetIds = useMemo(() => (
        (layout.order || []).filter((id) => {
            const item = widgetCatalog[id];
            return item && item.available && !(layout.hidden || []).includes(id);
        })
    ), [layout, widgetCatalog]);

    const hiddenAvailableWidgets = useMemo(() => (
        DEFAULT_WIDGET_ORDER
            .filter((id) => (layout.hidden || []).includes(id))
            .map((id) => widgetCatalog[id])
            .filter((item) => item && item.available)
    ), [layout.hidden, widgetCatalog]);

    const moveWidget = useCallback((id, direction) => {
        setLayout((prev) => {
            const hidden = prev.hidden || [];
            const visible = (prev.order || []).filter((wid) => {
                const item = widgetCatalog[wid];
                return item && item.available && !hidden.includes(wid);
            });
            const visIndex = visible.indexOf(id);
            const targetVis = visIndex + direction;
            if (visIndex < 0 || targetVis < 0 || targetVis >= visible.length) return prev;
            const order = [...(prev.order || [])];
            const from = order.indexOf(visible[visIndex]);
            const to = order.indexOf(visible[targetVis]);
            if (from < 0 || to < 0) return prev;
            [order[from], order[to]] = [order[to], order[from]];
            const next = { ...prev, order };
            writeWidgetLayout(next);
            return next;
        });
    }, [widgetCatalog]);

    const hideWidget = useCallback((id) => {
        setLayout((prev) => {
            const hidden = Array.from(new Set([...(prev.hidden || []), id]));
            const next = { ...prev, hidden };
            writeWidgetLayout(next);
            return next;
        });
    }, []);

    const showWidget = useCallback((id) => {
        setLayout((prev) => {
            const hidden = (prev.hidden || []).filter((item) => item !== id);
            const order = prev.order.includes(id) ? prev.order : [...prev.order, id];
            const next = { order, hidden };
            writeWidgetLayout(next);
            return next;
        });
        setShowAddWidgetModal(false);
    }, []);

    const resetWidgetLayout = useCallback(() => {
        const next = { order: [...DEFAULT_WIDGET_ORDER], hidden: [] };
        persistLayout(next);
        setShowAddWidgetModal(false);
        setEditMode(false);
    }, [persistLayout]);

    const load = async (aliveRef) => {
        setLoading(true);
        setError("");
        try {
            const [infoRes, news, convs, reqs, billingRowsRes, sockets, devices, locations, polls, sensors] = await Promise.all([
                apiFetch(`${API_BASE}/operator-info/operator`),
                fetchOperatorNews().catch(() => []),
                getToMeConversations().catch(() => []),
                listOperatorRequests({ status: "open" }).catch(() => []),
                listClientBilling().catch(() => []),
                fetchSockets().catch(() => []),
                fetchDevices().catch(() => []),
                fetchLocations().catch(() => []),
                fetchOperatorPolls().catch(() => []),
                sensorAccess.page_enabled ? listOperatorSensors().catch(() => []) : Promise.resolve([]),
            ]);

            let info = [];
            try {
                if (infoRes && infoRes.ok) info = await infoRes.json();
            } catch (_) {
                info = [];
            }

            const rows = Array.isArray(billingRowsRes) ? billingRowsRes : [];
            const dueSum = rows.reduce((acc, r) => acc + (Number(r?.amount_due) || 0), 0);
            const debtSum = rows.reduce((acc, r) => acc + (Number(r?.debt_amount) || 0), 0);
            const activeNewsCount = Array.isArray(news)
                ? news.filter((item) => item && item.status === true).length
                : 0;
            const doorEntries = buildDoorEntries({ devices, locations, sockets });
            const publicSensors = Array.isArray(sensors) ? sensors.filter((item) => item && item.is_private === false) : [];
            const publicAlerts = publicSensors.filter((item) => ["critical", "warning"].includes(String(item?.last_status || "").toLowerCase()));

            if (aliveRef && !aliveRef.current) return;

            setInfoCount(Array.isArray(info) ? info.length : 0);
            setNewsCount(activeNewsCount);
            setActiveChatsCount(Array.isArray(convs) ? convs.length : 0);
            setOpenRequestsCount(Array.isArray(reqs) ? reqs.length : 0);

            const now = Date.now();
            const activePolls = Array.isArray(polls)
                ? polls.filter((p) => p && p.status !== false && p.expires_at && new Date(p.expires_at).getTime() > now)
                : [];
            setActivePollsCount(activePolls.length);
            setPublicSensorsCount(publicSensors.length);
            setPublicSensorAlertsCount(publicAlerts.length);

            setAmountDueNetwork(dueSum);
            setDebtNetwork(debtSum);
            setBillingRows(rows);
            setDoorSockets(doorEntries);
        } catch (e) {
            if (aliveRef && !aliveRef.current) return;
            setError(e?.message || "Не вдалося завантажити віджети");
        } finally {
            if (!aliveRef || aliveRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        const aliveRef = { current: true };
        load(aliveRef);
        return () => {
            aliveRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sensorAccess.page_enabled]);

    useEffect(() => {
        if (editMode) {
            setDebtPanelOpen(false);
            setDoorPanelOpen(false);
        }
    }, [editMode]);

    return (
        <div className={`widgets-page ${editMode ? "widgets-page--editing" : ""}`}>
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        type="button"
                        className={`add-btn toolbar-icon-btn ${editMode ? "is-active is-save" : ""}`}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            setEditMode((v) => {
                                const next = !v;
                                if (next) markLayoutHintSeen();
                                return next;
                            });
                            setShowAddWidgetModal(false);
                        }}
                        onPointerUp={(e) => e.currentTarget.blur()}
                        aria-label={editMode ? "Зберегти розташування" : "Налаштувати віджети"}
                        title={editMode ? "Зберегти" : "Налаштувати віджети"}
                        aria-pressed={editMode}
                    >
                        {editMode ? <Save size={16} aria-hidden="true" /> : <Settings size={16} aria-hidden="true" />}
                    </button>
                    {editMode ? (
                        <button
                            type="button"
                            className="add-btn toolbar-icon-btn"
                            onClick={() => setShowAddWidgetModal(true)}
                            onPointerUp={(e) => e.currentTarget.blur()}
                            aria-label="Додати віджет"
                            title="Додати віджет"
                            disabled={hiddenAvailableWidgets.length === 0}
                        >
                            <Plus size={16} aria-hidden="true" />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="add-btn refresh toolbar-icon-btn"
                        onClick={(e) => {
                            e.currentTarget.blur();
                            load();
                        }}
                        onPointerUp={(e) => e.currentTarget.blur()}
                        disabled={loading}
                        aria-label="Оновити"
                        title="Оновити"
                    >
                        <RotateCw size={16} aria-hidden="true" />
                    </button>
                </div>
            </div>

            {editMode ? (
                <div className="widgets-edit-hint">
                    Режим налаштування: ↑↓ змінюють порядок, ✕ приховує блок. Дискетка зберігає й виходить, + додає приховані.
                </div>
            ) : !layoutHintSeen ? (
                <div className="widgets-edit-hint widgets-edit-hint--soft">
                    Налаштувати головну: кнопка шестерні ⚙ у панелі — порядок, приховати або додати блоки.
                </div>
            ) : null}

            {error ? <div className="message-banner" style={{ marginBottom: 12 }}>{error}</div> : null}
            {doorMessage ? <div className="message-banner" style={{ marginBottom: 12 }}>{doorMessage}</div> : null}
            {loading ? <p>⏳ Завантаження...</p> : null}

            <div className="widgets-layout">
                {visibleWidgetIds.map((id, index) => {
                    const item = widgetCatalog[id];
                    if (!item) return null;
                    const canMoveUp = index > 0;
                    const canMoveDown = index < visibleWidgetIds.length - 1;
                    return (
                        <div
                            key={id}
                            className={`widgets-layout__item${item.fullWidth ? " widgets-layout__item--full" : ""}`}
                        >
                            <WidgetCard
                                title={item.title}
                                value={item.value}
                                hint={item.hint}
                                onClick={item.onClick}
                                className={item.className}
                                hasBadge={item.hasBadge}
                                badgeValue={item.badgeValue}
                                editMode={editMode}
                                onMoveUp={() => moveWidget(id, -1)}
                                onMoveDown={() => moveWidget(id, 1)}
                                onHide={() => hideWidget(id)}
                                canMoveUp={canMoveUp}
                                canMoveDown={canMoveDown}
                            />
                        </div>
                    );
                })}
            </div>

            {debtPanelOpen && !editMode && (
                <div className="widgets-debt-panel">
                    <div className="widgets-debt-panel__header">
                        <strong>Клієнти з боргом</strong>
                        <div className="widgets-debt-panel__actions">
                            <button
                                type="button"
                                className="widgets-debt-panel__export"
                                onClick={() => {
                                    try {
                                        exportDebtorsPdf(debtors, {
                                            title: "Список боржників",
                                            networkName: "",
                                        });
                                    } catch (err) {
                                        setError(err?.message || "Не вдалося експортувати PDF");
                                    }
                                }}
                                disabled={debtors.length === 0}
                                aria-label="Експорт PDF"
                                title="Експорт PDF / друк"
                            >
                                <FileDown size={16} />
                                <span>PDF</span>
                            </button>
                            <button
                                type="button"
                                className="widgets-debt-panel__close"
                                onClick={() => setDebtPanelOpen(false)}
                                aria-label="Закрити"
                                title="Закрити"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                    {debtors.length === 0 ? (
                        <div className="widgets-debt-panel__empty">Немає клієнтів із боргом.</div>
                    ) : (
                        <div className="widgets-debt-panel__list">
                            {debtors.map((row, idx) => {
                                const key = `${row?.client_id || "c"}-${row?.context_id ?? "legacy"}-${row?.location_id ?? "none"}-${row?.apartment || ""}-${idx}`;
                                const meta = [
                                    row?.location_name,
                                    row?.apartment ? `кв. ${row.apartment}` : null,
                                ].filter(Boolean).join(" · ");
                                return (
                                    <div key={key} className="widgets-debt-panel__row">
                                        <div className="widgets-debt-panel__client">
                                            <strong>{row?.client_name || "Без імені"}</strong>
                                            {meta ? <span>{meta}</span> : null}
                                        </div>
                                        <div className="widgets-debt-panel__amount">
                                            {formatMoney(row?.debt_amount)} грн
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {doorPanelOpen && doorSockets.length > 1 && !editMode && (
                <div className="widgets-door-panel">
                    <div className="widgets-door-panel__header">
                        <strong>Оберіть підʼїзд:</strong>
                    </div>
                    <div className="widgets-door-panel__grid">
                        {doorSockets.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                className="widgets-door-panel__btn"
                                onClick={() => handleQuickUnlock(s.id)}
                                disabled={loading}
                                title="Натисніть щоб відкрити"
                            >
                                {s.label || s.device_name || `Підʼїзд #${s.id}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {showAddWidgetModal ? (
                <div className="modal-backdrop" onClick={() => setShowAddWidgetModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати віджет</h3>
                        {hiddenAvailableWidgets.length === 0 ? (
                            <p>Немає прихованих віджетів для додавання.</p>
                        ) : (
                            <div className="widgets-add-list">
                                {hiddenAvailableWidgets.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className="widgets-add-list__btn"
                                        onClick={() => showWidget(item.id)}
                                    >
                                        <span>{item.title}</span>
                                        <Plus size={16} />
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="modal-actions">
                            <button type="button" className="add-btn add-btn--secondary" onClick={resetWidgetLayout}>
                                Скинути все
                            </button>
                            <button type="button" className="add-btn" onClick={() => setShowAddWidgetModal(false)}>
                                Закрити
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default OperatorWidgets;
